import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  BookHeart,
  CalendarPlus,
  CheckCheck,
  ImagePlus,
  MessageSquare,
  Trash2,
  UserRoundPen,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../lib/auth";
import { colorOfUser } from "../lib/color";
import { fetchMembers } from "../lib/media/members";
import {
  fetchMediaByIds,
  fetchNotifications,
  fetchThumbnailKeys,
  markAllRead,
  markRead,
} from "../lib/media/notifications";
import {
  describeEntry,
  groupNotifications,
  relativeTimeOf,
  type NotificationRow,
} from "../lib/media/notify";
import type { NotificationKind } from "../lib/media/notify";
import { useSignedUrls } from "../lib/media/useSignedUrls";
import { fetchCoupleProfiles } from "../lib/profileItems";
import { useProfileNav } from "../lib/profileNav";
import { queryKeys } from "../lib/queryKeys";
import { useScheduleNav } from "../lib/scheduleNav";
import { useToast } from "../lib/toast";
import { useCloseOnBack } from "../lib/useCloseOnBack";
import MediaDetail from "../routes/MediaDetail";
import Trash from "../routes/Trash";
import QueryState from "./QueryState";

const ICONS: Record<NotificationKind, typeof ImagePlus> = {
  media_added: ImagePlus,
  media_deleted: Trash2,
  comment_added: MessageSquare,
  event_added: CalendarPlus,
  diary_entry_added: BookHeart,
  profile_changed: UserRoundPen,
};

const LIST_KEY = ["notifications", "all"];

export default function NotificationSheet({ onClose }: { onClose: () => void }) {
  useCloseOnBack(onClose);
  const queryClient = useQueryClient();
  const toast = useToast();
  const { session } = useAuth();
  const { goToEvent } = useScheduleNav();
  const { goToPartnerProfile } = useProfileNav();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // 背後の本文へスクロールを抜けさせない。CSS の touch-action は
  // 「リストが短くてスクロールが起きない」とき効かず(iOS はページスクロールに
  // 対して無視する場面もある)、指の動きがページに渡ってしまう。
  // touchmove を自前で止め、リストが本当に溢れているときだけリスト内の指を通す。
  // React の onTouchMove は passive で preventDefault が効かないため、自前で張る
  useEffect(() => {
    const node = rootRef.current;
    if (node === null) return;
    const onTouchMove = (event: TouchEvent) => {
      const list = listRef.current;
      if (
        list !== null &&
        event.target instanceof Node &&
        list.contains(event.target) &&
        list.scrollHeight > list.clientHeight
      ) {
        return;
      }
      event.preventDefault();
    };
    node.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      node.removeEventListener("touchmove", onTouchMove);
    };
  }, []);
  // まとめた通知は、まとめた分をそのまま開く。前後に送れる
  const [opened, setOpened] = useState<{
    ids: string[];
    currentId: string;
    /** コメント通知から来たときは、コメント欄まで開いた状態で出す */
    comments: boolean;
  } | null>(null);
  // 削除通知の行き先。消えたものは詳細ではなくゴミ箱で確かめてもらう
  const [trashOpen, setTrashOpen] = useState(false);

  // 開いている間は取り直さない。既読を付けた瞬間に「新着」の印が消えると、
  // 何が新しかったのか分からなくなる
  const notifications = useQuery({
    queryKey: LIST_KEY,
    queryFn: fetchNotifications,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const members = useQuery({ queryKey: ["members"], queryFn: fetchMembers });
  const profiles = useQuery({ queryKey: queryKeys.coupleProfiles, queryFn: fetchCoupleProfiles });

  const entries = useMemo(() => groupNotifications(notifications.data ?? []), [notifications.data]);
  const unreadIds = entries.filter((entry) => entry.unread).flatMap((entry) => entry.rowIds);

  // 行に添えるサムネイル(先頭の1枚)。削除通知には付けない(消えたものは見せない)
  const thumbTargets = useMemo(
    () =>
      entries
        .filter((entry) => entry.kind !== "media_deleted")
        .map((entry) => entry.mediaIds[0])
        .filter((id): id is string => id !== undefined),
    [entries],
  );
  const thumbRows = useQuery({
    queryKey: ["notification-thumbs", thumbTargets],
    queryFn: () => fetchThumbnailKeys(thumbTargets),
    enabled: thumbTargets.length > 0,
  });
  const keyOfMedia = useMemo(
    () => new Map((thumbRows.data ?? []).map((row) => [row.id, row.thumbnail_key])),
    [thumbRows.data],
  );
  const thumbs = useSignedUrls(useMemo(() => [...keyOfMedia.values()], [keyOfMedia]));

  /** 手元の一覧にも既読を反映する。開いている間は取り直さないため */
  function markLocally(ids: string[]) {
    const readAt = new Date().toISOString();
    const target = new Set(ids);
    queryClient.setQueryData<NotificationRow[]>(LIST_KEY, (current) =>
      (current ?? []).map((row) =>
        target.has(row.id) && row.read_at === null ? { ...row, read_at: readAt } : row,
      ),
    );
    void queryClient.invalidateQueries({ queryKey: ["notifications", "unread"] });
  }

  async function read(ids: string[]) {
    markLocally(ids);
    try {
      await markRead(ids);
    } catch (e) {
      // 見た目は既読のままにする。付け直しは次に開いたときに任せる
      console.error("既読付けに失敗", e);
      toast("既読に失敗しました。", "error");
    }
  }

  async function readAll() {
    const count = entries.filter((entry) => entry.unread).length;
    markLocally(unreadIds);
    try {
      await markAllRead();
    } catch (e) {
      console.error("まとめて既読に失敗", e);
      toast(`${String(count)}件の既読に失敗しました。`, "error");
    }
  }

  // 通知から辿ったメディア。タイムラインを経由せずに直接開く
  const openedMedia = useQuery({
    queryKey: ["media-by-ids", opened?.ids],
    queryFn: () => fetchMediaByIds(opened?.ids ?? []),
    enabled: opened !== null,
  });

  return (
    // どの幅でもベルの真下に開く。通知は OS の通知シェードと同じく「上から来る」もの。
    // スマホはヘッダー直下の全幅パネル、md 以上は幅を絞ったドロップダウンになる
    <div ref={rootRef} className="fixed inset-0 z-55 bg-black/40 md:bg-transparent">
      <button type="button" aria-label="閉じる" className="absolute inset-0" onClick={onClose} />

      {/* md〜xl 未満は本文がコンテンツ幅で中央寄せなので、右端も画面ではなく
          コンテンツ列の右端((100vw - コンテンツ幅) / 2 + ヘッダー余白)に合わせる */}
      <div className="notif-drop absolute inset-x-0 top-[calc(3.75rem+env(safe-area-inset-top))] flex max-h-[70vh] flex-col overflow-hidden rounded-b-2xl border-b border-[var(--color-line)] bg-white shadow-lg md:inset-x-auto md:top-16 md:right-[calc((100vw-var(--content-w))/2+1rem)] md:w-[var(--popup-w)] md:rounded-xl md:border xl:right-6">
        <header className="flex items-center justify-between border-b border-[var(--color-line)] p-4">
          <h2 className="flex items-center gap-2 text-sm font-medium">
            <Bell className="size-4 text-[var(--color-accent)]" aria-hidden />
            通知
          </h2>
          <div className="flex items-center gap-4">
            {unreadIds.length > 0 && (
              <button
                type="button"
                onClick={() => void readAll()}
                className="-my-1 flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-[var(--color-sub)] transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)] hover:text-[var(--color-ink)]"
              >
                <CheckCheck className="size-4" aria-hidden />
                まとめて既読
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="閉じる"
              className="-m-2 flex size-9 items-center justify-center rounded-full transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)]"
            >
              <X className="size-5 text-[var(--color-sub)]" aria-hidden />
            </button>
          </div>
        </header>

        <div ref={listRef} className="flex flex-col overflow-y-auto overscroll-contain pb-3">
          <QueryState
            isPending={notifications.isPending}
            isError={notifications.isError}
            errorMessage="通知の取得に失敗しました。"
            className="px-4 py-6 text-center text-sm"
            isEmpty={notifications.isSuccess && entries.length === 0}
            emptyMessage="通知がありません。"
            emptyClassName="px-4 py-8 text-center text-sm"
          />

          {entries.map((entry) => {
            const Icon = ICONS[entry.kind];
            const actorName = members.data?.get(entry.actorId) ?? "";
            const actorColor = colorOfUser(profiles.data, entry.actorId, session?.user.id);
            const firstId = entry.mediaIds[0];
            const thumbKey = firstId === undefined ? undefined : keyOfMedia.get(firstId);
            const thumbUrl = thumbKey === undefined ? undefined : thumbs.urls[thumbKey];
            return (
              <button
                key={entry.id}
                type="button"
                onClick={() => {
                  void read(entry.rowIds);
                  // 削除されたものは詳細を開けないので、ゴミ箱で確かめてもらう
                  if (entry.kind === "media_deleted") {
                    setTrashOpen(true);
                    return;
                  }
                  // 予定タブへ切り替わるので、このシートは閉じてから遷移する
                  if (entry.kind === "event_added") {
                    if (entry.eventId !== null) goToEvent(entry.eventId);
                    onClose();
                    return;
                  }
                  // プロフィールタブの相手側表示へ切り替わるので、同様に閉じてから遷移する
                  if (entry.kind === "profile_changed") {
                    goToPartnerProfile();
                    onClose();
                    return;
                  }
                  const [first] = entry.mediaIds;
                  if (first !== undefined) {
                    setOpened({
                      ids: entry.mediaIds,
                      currentId: first,
                      comments: entry.kind === "comment_added",
                    });
                  }
                }}
                className={`flex items-start gap-3 border-b border-[var(--color-line)] px-4 py-3 text-left transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,var(--color-card))] ${
                  entry.unread ? "bg-[var(--color-bg)]" : ""
                }`}
              >
                <Icon
                  className="mt-0.5 size-4 shrink-0"
                  style={{ color: actorColor }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm">{describeEntry(entry, actorName)}</span>
                  {entry.body !== null && (
                    <span className="mt-0.5 block truncate text-xs text-[var(--color-sub)]">
                      {entry.body}
                    </span>
                  )}
                  <span className="mt-0.5 block text-[11px] text-[var(--color-sub)]">
                    {relativeTimeOf(entry.createdAt)}
                  </span>
                </span>
                {thumbUrl !== undefined && (
                  <span className="relative size-10 shrink-0 overflow-hidden rounded-md">
                    <img
                      src={thumbUrl}
                      alt=""
                      loading="lazy"
                      className="size-full object-cover"
                      onError={thumbs.refresh}
                    />
                    {entry.mediaIds.length > 1 && (
                      <span className="absolute inset-0 flex items-center justify-center bg-black/40 text-[10px] text-white">
                        +{entry.mediaIds.length - 1}
                      </span>
                    )}
                  </span>
                )}
                {entry.unread && (
                  <span className="mt-1.5 size-2 shrink-0 rounded-full bg-[var(--color-accent)]" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {opened !== null && openedMedia.data !== undefined && openedMedia.data.length > 0 && (
        <MediaDetail
          items={openedMedia.data}
          currentId={opened.currentId}
          initialSheetOpen={opened.comments}
          onChange={(id) => {
            setOpened((current) => (current === null ? null : { ...current, currentId: id }));
          }}
          onClose={() => {
            setOpened(null);
          }}
        />
      )}

      {trashOpen && (
        <Trash
          onClose={() => {
            setTrashOpen(false);
          }}
        />
      )}
    </div>
  );
}
